<!--
Improved Select Modal Component with some nice features
- Search bar
- Single and multi-select modes
- Customizable trigger element with nice default
-->
<template>
	<!-- Select/Trigger element -->
	<div class="w-full cursor-pointer" @click="openModal">
		<slot>
			<template v-if="props.selectElementVariant === 'ionic'">
				<fake-ion-select
					:label="props.label"
					:select-element-text="selectElementText"
					:placeholder="props.placeholder"
					:error="props.error"
				>
					<slot name="selectElementValue"> </slot>
				</fake-ion-select>
			</template>
			<template v-else>
				<sg-select-element
					:value="selectElementText"
					:label="props.label"
					:placeholder="props.placeholder"
					:error="props.error"
				>
					<slot name="selectElementValue"> </slot>
				</sg-select-element>
			</template>
		</slot>
	</div>

	<!-- Selection Modal -->
	<ion-modal :is-open="modalOpen" @did-dismiss="cancelChanges">
		<ion-header>
			<ion-toolbar>
				<ion-buttons slot="start">
					<ion-button @click="cancelChanges">{{
						$t("common.cancel")
					}}</ion-button>
					<ion-button
						v-if="unselectable && workingMulti?.size"
						expand="block"
						color="dark"
						@click="workingMulti = new Set()"
					>
						{{ $t("common.clear") }}
					</ion-button>
				</ion-buttons>
				<ion-title>{{ modalTitle }}</ion-title>
				<ion-buttons slot="end">
					<ion-button @click="confirmChanges">
						{{ $t("common.done") }}
					</ion-button>
				</ion-buttons>
			</ion-toolbar>
			<ion-toolbar>
				<ion-searchbar v-if="searchbar" v-model="searchtext" />
			</ion-toolbar>
		</ion-header>

		<ion-content color="light" class="ion-padding">
			<ion-list class="flex h-full rounded-xl">
				<RecycleScroller
					v-slot="{ item }"
					:items="displayItems"
					:item-size="48"
					key-field="id"
					class="min-h-0 flex-1 overflow-y-auto"
				>
					<ion-item>
						<ion-checkbox
							:value="item.id"
							:checked="isSelected(item.id)"
							@ion-change="handleClick"
						>
							<div
								:style="{
									backgroundColor: item.color,
									color: item.textColor,
								}"
								:class="
									item.badge
										? 'rounded-full px-2 text-sm'
										: ''
								"
							>
								{{ item.text }}
							</div>
						</ion-checkbox>
					</ion-item>
				</RecycleScroller>
			</ion-list>
		</ion-content>
	</ion-modal>
</template>

<script setup lang="ts" generic="TId extends string | number">
	import {
		IonButton,
		IonButtons,
		IonCheckbox,
		IonContent,
		IonHeader,
		IonItem,
		IonList,
		IonModal,
		IonTitle,
		IonSearchbar,
		IonToolbar,
		CheckboxCustomEvent,
	} from "@ionic/vue";
	import { computed, Ref, ref } from "vue";
	import { RecycleScroller } from "vue-virtual-scroller";
	import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
	import SgSelectElement from "@/components/ui/SgSelectElement.vue";
	import FakeIonSelect from "@/components/ui/FakeIonSelect.vue";

	/** A single option/item for the select modal */
	interface SelectModalOption {
		id: TId;
		text: string;
		shortText?: string;
		/** Background color for the item */
		color?: string;
		textColor?: string;
		/** Changes the style of the item to be like a rounded chip or badge */
		badge?: boolean;
	}
	interface SelectModalProps {
		items: SelectModalOption[];
		label?: string;
		placeholder?: string;
		modalTitle?: string;
		/** Can you unselect all values of the modal (clear it) */
		unselectable?: boolean;
		/** Show a searchbar at the top of the select modal */
		searchbar?: boolean;
		selectElementVariant?: "ionic" | "seedgreen";
		/** Error message to display below the select */
		error?: string;
	}
	const props = withDefaults(defineProps<SelectModalProps>(), {
		label: undefined,
		placeholder: "Select",
		modalTitle: "Select",
		unselectable: true,
		searchbar: true,
		selectElementVariant: "seedgreen",
		error: undefined,
	});
	defineExpose({ openModal });

	const singleModel = defineModel<TId | undefined>("single");
	const multiModel = defineModel<Set<TId>>("multi");

	const multiSelectMode = computed(() => multiModel.value !== undefined);
	const searchtext = ref("");

	const selectElementText = computed(() => {
		if (multiSelectMode.value) {
			const selectedItems = props.items.filter((item) =>
				multiModel.value?.has(item.id),
			);
			if (selectedItems.length === 1) {
				return selectedItems[0].shortText ?? selectedItems[0].text;
			} else if (selectedItems.length > 1)
				return `${selectedItems.length} selected`;
		} else {
			const selectedItem = props.items.find(
				(item) => item.id === singleModel.value,
			);
			if (selectedItem)
				return selectedItem.shortText ?? selectedItem.text;
		}

		return undefined;
	});

	const displayItems = computed(() => {
		const search = searchtext.value.toLowerCase();
		return props.items.filter((item) =>
			item.text.toLowerCase().includes(search),
		);
	});

	/** Local working values that aren't committed until user clicks "Done" */
	const workingSingle = ref<TId | undefined>(singleModel.value) as
		| Ref<TId | undefined>
		| undefined;
	const workingMulti = ref(multiModel.value) as Ref<Set<TId>> | undefined;

	// Modal Controls
	const modalOpen = ref(false);
	function openModal() {
		if (modalOpen.value) {
			console.warn("Tried to open SelectModal: Already open");
		}

		if (multiSelectMode.value && workingMulti) {
			workingMulti.value = new Set(multiModel.value);
		} else if (workingSingle) {
			workingSingle.value = singleModel.value;
		}
		modalOpen.value = true;
	}

	function cancelChanges() {
		if (modalOpen.value) modalOpen.value = false;
	}

	function confirmChanges() {
		if (multiSelectMode.value && workingMulti) {
			multiModel.value = new Set(workingMulti.value);
		} else if (workingSingle) {
			singleModel.value = workingSingle.value;
		}
		modalOpen.value = false;
	}

	function isSelected(id: TId) {
		if (multiSelectMode.value && workingMulti) {
			return workingMulti.value.has(id);
		} else if (workingSingle) {
			return workingSingle.value === id;
		}
		return false;
	}

	function handleClick(event: CheckboxCustomEvent<TId>) {
		const id = event.detail.value;
		const checked = event.detail.checked;

		if (multiSelectMode.value) {
			setMultiValue(id, checked, event);
		} else {
			setSingleValue(id, checked, event);
		}
	}

	/**
	 * Set the value of the select if we are in single-select mode
	 */
	function setSingleValue(
		id: TId,
		checked: boolean,
		event: CheckboxCustomEvent<TId>,
	) {
		if (!workingSingle) return;

		if (!checked && !props.unselectable) {
			event.target.checked = true;
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (checked) {
			workingSingle.value = id;
		} else {
			workingSingle.value = undefined;
		}
	}

	/**
	 * Set the value of the select if we are in multi-select mode
	 */
	function setMultiValue(
		id: TId,
		checked: boolean,
		event: CheckboxCustomEvent<TId>,
	) {
		if (!workingMulti) return;

		if (!checked && !props.unselectable && workingMulti.value.size === 1) {
			event.target.checked = true;
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		if (checked) {
			workingMulti.value.add(id);
		} else {
			workingMulti.value.delete(id);
		}
	}
</script>
